/**
 * Breadcrumbs from the address and the page registry:
 *   /loans                              Loans / Home
 *   /loans/configuration                Loans / Configuration
 *   /loans/access/people                Loans / Access and roles / People
 *   /hub/access/settings/hub.admin.upns Innovation Hub / Access and roles / Access settings / hub.admin.upns
 * A path deeper than its page (a record with its own address) ends in that record.
 */

import type { Crumb, ShellNav, ShellPage, ShellSection } from './types';

function trimSlash(p: string): string {
  return p.length > 1 ? p.replace(/\/+$/, '') : p;
}

/** The page a path belongs to: the longest registry path equal to it or a parent of it. */
export function findPage(pathname: string, nav: ShellNav): { page: ShellPage; section: ShellSection } | null {
  const path = trimSlash(pathname);
  let best: { page: ShellPage; section: ShellSection } | null = null;
  for (const section of nav.sections) {
    for (const page of section.pages) {
      if (page.external) continue;
      const pp = trimSlash(page.path.split('?')[0] ?? page.path);
      if (path === pp || path.startsWith(`${pp}/`)) {
        if (!best || pp.length > trimSlash(best.page.path.split('?')[0] ?? best.page.path).length) best = { page, section };
      }
    }
  }
  return best;
}

/** "deposit-rates" -> "Deposit rates"; keys with dots or digits are left as they are. */
export function prettySegment(seg: string): string {
  let s = seg;
  try {
    s = decodeURIComponent(seg);
  } catch {
    /* keep raw */
  }
  if (/^[a-z]+(-[a-z]+)*$/.test(s)) {
    const words = s.replace(/-/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return s;
}

export function deriveBreadcrumbs(pathname: string, nav: ShellNav, opts: { leafLabel?: string } = {}): Crumb[] {
  const path = trimSlash(pathname);
  const root = trimSlash(nav.root.path);
  const rootCrumb: Crumb = { label: nav.root.label, path: nav.root.path };

  if (path === root || (root === '/' && path === '')) return [rootCrumb, { label: 'Home' }];

  const hit = findPage(path, nav);
  if (hit) {
    const { page, section } = hit;
    const pagePath = trimSlash(page.path.split('?')[0] ?? page.path);
    const crumbs: Crumb[] = [rootCrumb, { label: section.label, path: section.path }];
    if (path === pagePath) {
      crumbs.push({ label: page.label });
      return crumbs;
    }
    crumbs.push({ label: page.label, path: page.path });
    const rest = path.slice(pagePath.length + 1).split('/').filter(Boolean);
    const leaf = opts.leafLabel ?? prettySegment(rest[rest.length - 1] ?? '');
    if (leaf) crumbs.push({ label: leaf });
    return crumbs;
  }

  // A section landing (e.g. /retail/configuration).
  const section = nav.sections.find((s) => s.path && trimSlash(s.path) === path);
  if (section) return [rootCrumb, { label: section.label }];

  const segs = path.slice(root === '/' ? 1 : root.length + 1).split('/').filter(Boolean);
  if (path.startsWith(root) && segs.length > 0) {
    // A section id in the address without a landing page of its own in the registry.
    const bySeg = nav.sections.find((s) => s.id === segs[0]);
    if (bySeg && segs.length === 1) return [rootCrumb, { label: bySeg.label }];
    return [rootCrumb, { label: opts.leafLabel ?? prettySegment(segs[segs.length - 1] ?? '') }];
  }
  return [{ label: opts.leafLabel ?? prettySegment(path.split('/').filter(Boolean).pop() ?? 'Home') }];
}
